import { useMemo, useRef } from 'react';

/**
 * Nests a flat list into a tree by parent id.
 *
 * Rows that have not changed keep the node they were given last time, so that a row added or
 * replaced somewhere in the list does not hand every other row a new object. That matters because
 * the table memoizes a row's output against the row itself: rebuilding all of them is the difference
 * between rendering what appeared and re-rendering the whole page.
 *
 * ⚠️ A row must therefore be replaced rather than changed in place. A row edited in place keeps its
 * identity, and the table would go on showing what it held before.
 *
 * A row whose parent is not in the list is left out of the tree, and only a parent id of exactly
 * null makes a row a root.
 *
 * @param {object[]} data - Flat rows
 * @param {string} idColumn
 * @param {string} parentIdColumn
 * @param {Map<object, {built: object, kids: object[]}>} prev - Nodes built last time, keyed on the
 *   row each was built from
 * @param {Map<object, {built: object, kids: object[]}>} next - The same, for this build
 * @returns {{items: object[], isHierarchy: boolean}}
 */
const _parseToHierarchy = (data, idColumn, parentIdColumn, prev, next) => {
  const map = new Map();
  const roots = [];
  let isHierarchy = false;

  data.forEach(item => map.set(item[idColumn], { item, kids: [] }));

  data.forEach(item => {
    if (item[parentIdColumn] === null) {
      roots.push(map.get(item[idColumn]));
    } else {
      isHierarchy = true;
      const parent = map.get(item[parentIdColumn]);
      if (parent) parent.kids.push(map.get(item[idColumn]));
    }
  });

  const build = node => {
    const kids = node.kids.map(build);
    const cached = prev.get(node.item);

    // The row is the one it was, and so are its children: hand back the node built last time.
    if (
      cached &&
      cached.kids.length === kids.length &&
      cached.kids.every((k, i) => k === kids[i])
    ) {
      next.set(node.item, cached);
      return cached.built;
    }

    // A leaf carries no children property at all, which is what tells the table it has none.
    const built = kids.length ? { ...node.item, children: kids } : { ...node.item };
    next.set(node.item, { built, kids });

    return built;
  };

  return { items: roots.map(build), isHierarchy };
};

/**
 * Nests flat rows into a tree, for a table that renders a hierarchy.
 * @param {object[]} data - Flat rows
 * @param {string} idColumn
 * @param {string} parentIdColumn
 * @returns {{items: object[], isHierarchy: boolean}} `items` is the data unchanged when there is no
 *   id/parent pair to nest it by
 */
export const useHierarchialData = (data, idColumn, parentIdColumn) => {
  // Rebuilt on every run rather than added to, so it holds this tree's nodes and nothing else.
  const cacheRef = useRef(new Map());

  const hierarchialItems = useMemo(() => {
    // Early exit if data is not hierarchial
    if (!data || !(idColumn && parentIdColumn)) return { items: data, isHierarchy: false };

    const next = new Map();
    const result = _parseToHierarchy(data, idColumn, parentIdColumn, cacheRef.current, next);
    cacheRef.current = next;

    return result;
  }, [data, idColumn, parentIdColumn]);

  return {
    items: hierarchialItems.items,
    isHierarchy: hierarchialItems.isHierarchy,
  };
};
